import Immutable from 'immutable';
import { queryKeys, urlUtils } from '../../../utils/url';
import * as ActionTypes from '../../actionTypes/cruiseListActionTypes';

const filterGroups = {
  departureMonth: {
    order: 0,
    id: 'departureMonth',
    heading: '',
    options: [],
    selected: [],
  },
  shipRating: {
    order: 1,
    id: 'shipRating',
    heading: '',
    options: [],
    selected: [],
  },
  duration: {
    order: 2,
    id: 'duration',
    heading: '',
    toLabel: '',
    daysLabel: '',
    minValue: null,
    maxValue: null,
    selectedMinValue: null,
    selectedMaxValue: null,
  },
  cruiseCategory: {
    order: 3,
    id: 'cruiseCategory',
    heading: '',
    options: [],
    selected: [],
  },
  destination: {
    order: 4,
    id: 'destination',
    heading: '',
    numberOfDestinationsHeading: '',
    toLabel: '',
    destinationsLabel: '',
    options: [],
    minNumberOfDestinations: null,
    maxNumberOfDestinations: null,
    selected: [],
    selectedMinNumberOfDestinations: null,
    selectedMaxNumberOfDestinations: null,
  },
  shippingCompany: {
    order: 5,
    id: 'shippingCompany',
    heading: '',
    options: [],
    selected: [],
  },
};

const initialState = Immutable.fromJS({
  filterChangesCounter: 0,
  filterPanelIsVisible: false,
  filterGroups,
});

export default (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.INIT_SUCCESS:
      return state.withMutations((s) => {
        // Get query filter state
        const filterQueryParams = urlUtils.getCruiseFilterStateFromQuery(action.preSelectedFilter);

        const departureMonthInQuery = filterQueryParams[queryKeys.CRUISE_FILTERS.departureMonth]
          ? filterQueryParams[queryKeys.CRUISE_FILTERS.departureMonth].split(',')
          : null;
        const shipRatingInQuery = filterQueryParams[queryKeys.CRUISE_FILTERS.shipRating]
          ? filterQueryParams[queryKeys.CRUISE_FILTERS.shipRating].split(',').map(r => parseInt(r, 10))
          : null;
        const durationInQuery = filterQueryParams[queryKeys.CRUISE_FILTERS.duration]
          ? filterQueryParams[queryKeys.CRUISE_FILTERS.duration].split(',')
          : null;
        const minDurationInQuery = durationInQuery ? parseInt(durationInQuery[0], 10) : null;
        const maxDurationInQuery = durationInQuery ? parseInt(durationInQuery[1], 10) : null;
        const cruiseCategoryInQuery = filterQueryParams[queryKeys.CRUISE_FILTERS.cruiseCategory]
          ? filterQueryParams[queryKeys.CRUISE_FILTERS.cruiseCategory].split(',')
          : null;
        const destinationInQuery = filterQueryParams[queryKeys.CRUISE_FILTERS.destination]
          ? filterQueryParams[queryKeys.CRUISE_FILTERS.destination].split(',')
          : null;
        const nOfDestInQuery = filterQueryParams[queryKeys.CRUISE_FILTERS.numberOfDestinations]
          ? filterQueryParams[queryKeys.CRUISE_FILTERS.numberOfDestinations].split(',')
          : null;
        const minNOfDestInQuery = nOfDestInQuery ? parseInt(nOfDestInQuery[0], 10) : null;
        const maxNOfDestInQuery = nOfDestInQuery ? parseInt(nOfDestInQuery[1], 10) : null;
        const shippingCompanyInQuery = filterQueryParams[queryKeys.CRUISE_FILTERS.shippingCompany]
          ? filterQueryParams[queryKeys.CRUISE_FILTERS.shippingCompany].split(',')
          : null;

        // Add filter options to state.
        if (action.filter) {
          s.setIn(
            ['filterGroups', 'departureMonth', 'options'],
            action.filter.departureMonths,
          )
          s.setIn(
            ['filterGroups', 'shipRating', 'options'],
            action.filter.shipRatingOptions,
          );
          s.setIn(
            ['filterGroups', 'duration', 'minValue'],
            action.filter.minDuration,
          );
          s.setIn(
            ['filterGroups', 'duration', 'maxValue'],
            action.filter.maxDuration,
          );
          s.setIn(
            ['filterGroups', 'cruiseCategory', 'options'],
            Immutable.fromJS(
              action.filter.cruiseCategoryOptions.map((cruiseCategory) => {
                return { id: cruiseCategory.id, name: cruiseCategory.name };
              }),
            ),
          );
          s.setIn(
            ['filterGroups', 'destination', 'options'],
            Immutable.fromJS(
              action.filter.destinationOptions.map((destination) => {
                return { id: destination.id, name: destination.name };
              }),
            ),
          );
          s.setIn(
            ['filterGroups', 'destination', 'minNumberOfDestinations'],
            action.filter.minNumberOfDestinations,
          );
          s.setIn(
            ['filterGroups', 'destination', 'maxNumberOfDestinations'],
            action.filter.maxNumberOfDestinations,
          );
          s.setIn(
            ['filterGroups', 'shippingCompany', 'options'],
            Immutable.fromJS(
              action.filter.shippingCompanyOptions.map((shippingCompany) => {
                return { id: shippingCompany.id, name: shippingCompany.name };
              }),
            ),
          );
        }

        // Set filter values from query
        if (departureMonthInQuery) {
          s.setIn(
            ['filterGroups', 'departureMonth', 'selected'],
            Immutable.List(departureMonthInQuery),
          );
        }
        if (shipRatingInQuery) {
          s.setIn(
            ['filterGroups', 'shipRating', 'selected'],
            Immutable.List(shipRatingInQuery),
          );
        }
        if (durationInQuery) {
          s.setIn(
            ['filterGroups', 'duration', 'selectedMinValue'],
            minDurationInQuery,
          );
          s.setIn(
            ['filterGroups', 'duration', 'selectedMaxValue'],
            maxDurationInQuery,
          );
        }
        if (cruiseCategoryInQuery) {
          s.setIn(
            ['filterGroups', 'cruiseCategory', 'selected'],
            Immutable.List(cruiseCategoryInQuery),
          );
        }
        if (destinationInQuery) {
          s.setIn(
            ['filterGroups', 'destination', 'selected'],
            Immutable.List(destinationInQuery),
          );
        }
        if (nOfDestInQuery) {
          s.setIn(
            ['filterGroups', 'destination', 'selectedMinNumberOfDestinations'],
            minNOfDestInQuery,
          );
          s.setIn(
            ['filterGroups', 'destination', 'selectedMaxNumberOfDestinations'],
            maxNOfDestInQuery,
          );
        }
        if (shippingCompanyInQuery) {
          s.setIn(
            ['filterGroups', 'shippingCompany', 'selected'],
            Immutable.List(shippingCompanyInQuery),
          );
        }

        // Add filter labels to state.
        if (action.labels) {
          s.setIn(
            ['filterGroups', 'departureMonth', 'heading'],
            action.labels.filter_departureMonth_heading,
          );
          s.setIn(
            ['filterGroups', 'shipRating', 'heading'],
            action.labels.filter_shipRating_heading,
          );
          s.setIn(
            ['filterGroups', 'duration', 'heading'],
            action.labels.filter_duration_heading,
          );
          s.setIn(
            ['filterGroups', 'duration', 'toLabel'],
            action.labels.filter_duration_toLabel,
          );
          s.setIn(
            ['filterGroups', 'duration', 'dayLabel'],
            action.labels.filter_duration_dayLabel,
          );
          s.setIn(
            ['filterGroups', 'duration', 'daysLabel'],
            action.labels.filter_duration_daysLabel,
          );
          s.setIn(
            ['filterGroups', 'cruiseCategory', 'heading'],
            action.labels.filter_cruiseCategory_heading,
          );
          s.setIn(
            ['filterGroups', 'destination', 'heading'],
            action.labels.filter_destination_heading,
          );
          s.setIn(
            ['filterGroups', 'destination', 'numberOfDestinationsHeading'],
            action.labels.filter_destination_numberOfDestinationsHeading,
          );
          s.setIn(
            ['filterGroups', 'destination', 'toLabel'],
            action.labels.filter_destination_toLabel,
          );
          s.setIn(
            ['filterGroups', 'destination', 'destinationsLabel'],
            action.labels.filter_destination_destinationsLabel,
          );
          s.setIn(
            ['filterGroups', 'shippingCompany', 'heading'],
            action.labels.filter_shippingCompany_heading,
          );
        }
      });

    case ActionTypes.TOGGLE_FILTER_PANEL:
      return state.withMutations((s) => {
        s.set('filterPanelIsVisible', action.isVisible);
      });

    case ActionTypes.CLEAR_FILTERS:
      return state.withMutations((s) => {
        s.setIn(['filterGroups', 'departureMonth', 'selected'], Immutable.List(filterGroups.departureMonth.selected))
        s.setIn(['filterGroups', 'shipRating', 'selected'], Immutable.List(filterGroups.shipRating.selected));
        s.setIn(['filterGroups', 'duration', 'selectedMinValue'], filterGroups.duration.selectedMinValue);
        s.setIn(['filterGroups', 'duration', 'selectedMaxValue'], filterGroups.duration.selectedMaxValue);
        s.setIn(['filterGroups', 'cruiseCategory', 'selected'], Immutable.List(filterGroups.cruiseCategory.selected));
        s.setIn(['filterGroups', 'destination', 'selected'], Immutable.List(filterGroups.destination.selected));
        s.setIn(['filterGroups', 'destination', 'selectedMinNumberOfDestinations'], filterGroups.destination.selectedMinNumberOfDestinations);
        s.setIn(['filterGroups', 'destination', 'selectedMaxNumberOfDestinations'], filterGroups.destination.selectedMaxNumberOfDestinations);
        s.setIn(['filterGroups', 'shippingCompany', 'selected'], Immutable.List(filterGroups.shippingCompany.selected));

        s.set('filterChangesCounter', s.get('filterChangesCounter') + 1);
      });
    
    case ActionTypes.SELECT_FILTER_DEPARTURE_MONTH: {
      let copySelected = state.getIn([
        'filterGroups',
        'departureMonth',
        'selected',
      ]);

      const checked =
        copySelected &&
        copySelected.size > 0 &&
        copySelected.indexOf(action.selectedId) !== -1;
      if (checked) {
        copySelected = copySelected.splice(
          copySelected.indexOf(action.selectedId),
          1,
        );
      } else {
        copySelected = copySelected.push(action.selectedId);
      }

      return state.withMutations((s) => {
        s.setIn(
          ['filterGroups', 'departureMonth', 'selected'],
          Immutable.List(copySelected),
        );
        s.set('filterChangesCounter', s.get('filterChangesCounter') + 1);
      });
    }

    case ActionTypes.SELECT_FILTER_SHIP_RATING: {
      let copySelected = state.getIn([
        'filterGroups',
        'shipRating',
        'selected',
      ]);
      const checked =
        copySelected &&
        copySelected.size > 0 &&
        copySelected.indexOf(action.rating) !== -1;
      if (checked) {
        copySelected = copySelected.splice(
          copySelected.indexOf(action.rating),
          1,
        );
      } else {
        copySelected = copySelected.push(action.rating);
      }

      return state.withMutations((s) => {
        s.setIn(
          ['filterGroups', 'shipRating', 'selected'],
          Immutable.List(copySelected),
        );
        s.set('filterChangesCounter', s.get('filterChangesCounter') + 1);
      });
    }

    case ActionTypes.SELECT_FILTER_DURATION: {
      return state.withMutations((s) => {
        s.setIn(['filterGroups', 'duration', 'selectedMinValue'], action.lowValue);
        s.setIn(['filterGroups', 'duration', 'selectedMaxValue'], action.highValue);

        s.set('filterChangesCounter', s.get('filterChangesCounter') + 1);
      });
    }

    case ActionTypes.SELECT_FILTER_CRUISE_CATEGORY: {
      let copySelected = state.getIn([
        'filterGroups',
        'cruiseCategory',
        'selected',
      ]);

      const checked =
        copySelected &&
        copySelected.size > 0 &&
        copySelected.indexOf(action.selectedId) !== -1;
      if (checked) {
        copySelected = copySelected.splice(
          copySelected.indexOf(action.selectedId),
          1,
        );
      } else {
        copySelected = copySelected.push(action.selectedId);
      }

      return state.withMutations((s) => {
        s.setIn(
          ['filterGroups', 'cruiseCategory', 'selected'],
          Immutable.List(copySelected),
        );
        s.set('filterChangesCounter', s.get('filterChangesCounter') + 1);
      });
    }

    case ActionTypes.SELECT_FILTER_DESTINATION: {
      let copySelected = state.getIn([
        'filterGroups',
        'destination',
        'selected',
      ]);

      const checked =
        copySelected &&
        copySelected.size > 0 &&
        copySelected.indexOf(action.selectedId) !== -1;
      if (checked) {
        copySelected = copySelected.splice(
          copySelected.indexOf(action.selectedId),
          1,
        );
      } else {
        copySelected = copySelected.push(action.selectedId);
      }

      return state.withMutations((s) => {
        s.setIn(
          ['filterGroups', 'destination', 'selected'],
          Immutable.List(copySelected),
        );
        s.set('filterChangesCounter', s.get('filterChangesCounter') + 1);
      });
    }

    case ActionTypes.SELECT_FILTER_NUMBER_OF_DESTINATIONS: {
      return state.withMutations((s) => {
        s.setIn(['filterGroups', 'destination', 'selectedMinNumberOfDestinations'], action.lowValue);
        s.setIn(['filterGroups', 'destination', 'selectedMaxNumberOfDestinations'], action.highValue);

        s.set('filterChangesCounter', s.get('filterChangesCounter') + 1);
      });
    }

    case ActionTypes.SELECT_FILTER_SHIPPING_COMPANY: {
      let copySelected = state.getIn([
        'filterGroups',
        'shippingCompany',
        'selected',
      ]);

      const checked =
        copySelected &&
        copySelected.size > 0 &&
        copySelected.indexOf(action.selectedId) !== -1;
      if (checked) {
        copySelected = copySelected.splice(
          copySelected.indexOf(action.selectedId),
          1,
        );
      } else {
        copySelected = copySelected.push(action.selectedId);
      }

      return state.withMutations((s) => {
        s.setIn(
          ['filterGroups', 'shippingCompany', 'selected'],
          Immutable.List(copySelected),
        );
        s.set('filterChangesCounter', s.get('filterChangesCounter') + 1);
      });
    }

    default:
      return state;
  }
};
